import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../App';
import { Briefcase, Plus, Search, MoreHorizontal, Calendar, Users } from 'lucide-react';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';

const PracticeViewJobsPage = ({ user, currentUser }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [jobListings, setJobListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock data for now - replace with actual Firestore calls later
  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setJobListings([
        {
          id: '1',
          title: 'Dental Hygienist - Part Time',
          description: 'We are looking for an experienced dental hygienist to join our team...',
          employmentType: 'Part-time',
          status: 'open',
          postedAt: new Date('2024-01-15'),
          applicationsCount: 3
        },
        {
          id: '2',
          title: 'Dental Nurse - Full Time',
          description: 'Exciting opportunity for a qualified dental nurse...',
          employmentType: 'Full-time',
          status: 'open',
          postedAt: new Date('2024-01-10'),
          applicationsCount: 7
        },
        {
          id: '3',
          title: 'Locum Dentist',
          description: 'Weekend locum position available...',
          employmentType: 'Locum',
          status: 'closed',
          postedAt: new Date('2024-01-05'),
          applicationsCount: 12
        }
      ]);
      setLoading(false);
    }, 1000);
  }, [user]);

  const filteredJobs = jobListings.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading job postings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Page Header with proper spacing and theme-aware styling */}
      <div className="flex h-[60px] items-center justify-between">
        <div>
          <h1 className={cn(
            "text-3xl font-bold tracking-tight",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            Job Postings
          </h1>
          <p className={cn(
            "text-sm mt-1",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            Manage your practice job listings
          </p>
        </div>
        <Button 
          onClick={() => navigate(ROUTES.PRACTICE_JOB_NEW)}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Post New Job
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Search and Filter Section */}
      <Card className={cn(
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
      )}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search job titles and descriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn(
                  "pl-10",
                  theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#1F1F2E] border-gray-700'
                )}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={cn(
                "w-full sm:w-[180px]",
                theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#1F1F2E] border-gray-700'
              )}>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Job Listings */}
      {filteredJobs.length > 0 ? (
        <div className="space-y-4">
          {filteredJobs.map(job => (
            <Card key={job.id} className={cn(
              "transition-shadow hover:shadow-md",
              theme === 'light' ? 'bg-white border-gray-200 hover:shadow-gray-200' : 'bg-[#2A2A3E] border-purple-500/20 hover:shadow-purple-500/10'
            )}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={cn(
                        "text-xl font-semibold",
                        theme === 'light' ? 'text-purple-700' : 'text-purple-300'
                      )}>
                        {job.title}
                      </h3>
                      <span className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        job.status === 'open' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : job.status === 'closed'
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      )}>
                        {job.status}
                      </span>
                    </div>
                    <p className={cn(
                      "text-sm mb-3 line-clamp-2",
                      theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                    )}>
                      {job.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        <span>{job.employmentType}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>Posted {new Date(job.postedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{job.applicationsCount || 0} applications</span>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={cn(
          theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
        )}>
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className={cn(
              "text-lg font-medium mb-2",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              {searchTerm || statusFilter !== 'all' ? 'No matching jobs found' : 'No job postings yet'}
            </h3>
            <p className={cn(
              "text-sm mb-4",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search criteria'
                : 'Start by creating your first job posting'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button 
                onClick={() => navigate(ROUTES.PRACTICE_JOB_NEW)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Job Posting
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PracticeViewJobsPage;
